<template>
  <div class="report-body" v-if="reportData">
    <div class="header">
      <div class="report-title">
        <h1>FREED Credit Report</h1>
      </div>
    </div>

    <div class="report-details" v-if="reportData.bureauReportedDate">
      <span><strong>Report Date:</strong> {{ reportData.bureauReportedDate }}</span>
    </div>

    <div class="section">
      <div class="section-header active">Current Application Information</div>
      <div class="section-content">
        <table class="info-table">
          <tr v-if="reportData.accountHolderFullName">
            <td class="info-label">Name</td>
            <td class="info-value">
              <span v-for="(name, idx) in reportData.accountHolderFullName" :key="idx">{{ name }} </span>
            </td>
          </tr>
          <tr v-if="reportData.accountHolderInfoList && reportData.accountHolderInfoList[0] && reportData.accountHolderInfoList[0].dateOfBirth">
            <td class="info-label">Date Of Birth</td>
            <td class="info-value">{{ reportData.accountHolderInfoList[0].dateOfBirth }}</td>
          </tr>
          <tr v-if="reportData.CAIS_Holder_Address_Details">
            <td class="info-label">Address</td>
            <td class="info-value">
              <div v-for="(addr, idx) in reportData.CAIS_Holder_Address_Details" :key="idx">
                {{ addr.First_Line_Of_Address_non_normalized }}<br v-if="addr.First_Line_Of_Address_non_normalized">
                {{ addr.Second_Line_Of_Address_non_normalized }}<br v-if="addr.Second_Line_Of_Address_non_normalized">
                {{ addr.Third_Line_Of_Address_non_normalized }}<br v-if="addr.Third_Line_Of_Address_non_normalized">
                {{ addr.City_non_normalized }}<br v-if="addr.City_non_normalized">
                {{ addr.State_non_normalized }}<br v-if="addr.State_non_normalized">
                PIN: {{ addr.ZIP_Postal_Code_non_normalized }}<br v-if="addr.ZIP_Postal_Code_non_normalized">
                Country: {{ addr.CountryCode_non_normalized }}
              </div>
            </td>
          </tr>
          <tr v-if="reportData.accountHolderInfoList && reportData.accountHolderInfoList[0] && reportData.accountHolderInfoList[0].gender">
            <td class="info-label">Gender</td>
            <td class="info-value">{{ reportData.accountHolderInfoList[0].gender }}</td>
          </tr>
          <tr>
            <td class="info-label">PAN</td>
            <td class="info-value">
              <template v-if="reportData.panList && reportData.panList[0]">
                {{ reportData.panList[0] }}
              </template>
              <template v-else>---</template>
            </td>
          </tr>
          <tr v-if="reportData.accountHolderInfoList && reportData.accountHolderInfoList[0] && reportData.accountHolderInfoList[0].mobile">
            <td class="info-label">Mobile Phone</td>
            <td class="info-value">{{ reportData.accountHolderInfoList[0].mobile }}</td>
          </tr>
        </table>
      </div>
    </div>

    <div class="section">
      <div class="section-header active">Credit Report Summary</div>
      <div class="section-content">
        <div class="summary-boxes">
          <div class="summary-box">
            <span class="summary-label">Credit Score</span>
            <span class="summary-value">{{ reportData.creditBureauScore || 'N/A' }}</span>
          </div>
          <template v-if="reportData.accountsSummary">
            <div class="summary-box">
              <span class="summary-label">Total Accounts</span>
              <span class="summary-value">{{ reportData.accountsSummary.totalAccounts || 0 }}</span>
            </div>
            <div class="summary-box">
              <span class="summary-label">Active Accounts</span>
              <span class="summary-value">{{ reportData.accountsSummary.accountsActiveCount || 0 }}</span>
            </div>
            <div class="summary-box">
              <span class="summary-label">Closed Accounts</span>
              <span class="summary-value">{{ reportData.accountsSummary.accountsClosedCount || 0 }}</span>
            </div>
            <div class="summary-box">
              <span class="summary-label">Secured Balance</span>
              <span class="summary-value">{{ reportData.accountsSummary.securedAccountsTotalOutstanding || 0 }}</span>
            </div>
            <div class="summary-box">
              <span class="summary-label">UnSecured Balance</span>
              <span class="summary-value">{{ (reportData.accountsSummary.unsecuredAccountsTotalOutstanding || 0) | toINR }}</span>
            </div>
            <div class="summary-box">
              <span class="summary-label">Total Outstanding Balance</span>
              <span class="summary-value">{{ (reportData.accountsSummary.accountsTotalOutstanding || 0)| toINR }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="section">
      <div >
        <h1 class="main-title">Account Info Summary</h1>

        <div class="section-card">
          <div class="section-header active">Active Loans</div>
          <div class="section-content">
            <div v-for="type in ['SECURED', 'UNSECURED', 'OTHERS']" :key="type" class="loan-category">
              <div class="category-title">
                <i class="fas fa-money-check-alt"></i> {{ type === 'OTHERS' ? 'Other' : type.charAt(0) + type.slice(1).toLowerCase() }} Loans
              </div>
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Creditor Name</th>
                    <th>Account Number</th>
                    <th>Open Date</th>
                    <th>Account Status</th>
                    <th class="text-right">Sanctioned Amount</th>
                    <th class="text-right">Outstanding Amount</th>
                    <th>Last Payment Date</th>
                    <th>Loan Type</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="hasLoanData('ACTIVE', type)">
                    <tr v-for="(loan, idx) in reportData.preprocessed_accountInfo.ACTIVE[type]" :key="idx">
                      <td>{{ loan.lenderName }}</td>
                      <td>{{ loan.accountNumber }}</td>
                      <td>{{ loan.openDate }}</td>
                      <td><span class="status-badge status-active">Active</span></td>
                      <td class="text-right">{{ loan.sanctionedAmount| toINR }}</td>
                      <td class="text-right">{{ loan.outstandingAmount | toINR }}</td>
                      <td>{{ loan.lastPaymentDate }}</td>
                      <td><span class="loan-badge">{{ loan.debtType }}</span></td>
                    </tr>
                  </template>
                  <tr v-else>
                    <td colspan="8" class="no-data-available">No Data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- <div class="section-card">
          <div class="section-header closed">Closed Loans</div>
          <div class="section-content">
            <div class="loan-category">
              <div class="category-title"><i class="fas fa-times-circle"></i> Closed Accounts</div>
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Creditor Name</th>
                    <th>Account Number</th>
                    <th>Open Date</th>
                    <th>Account Status</th>
                    <th class="text-right">Current Balance</th>
                    <th class="text-right">Amount Past Due</th>
                    <th>Last Payment Date</th>
                    <th>Loan Type</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="hasLoanData('CLOSED', 'CLOSED')">
                    <tr v-for="(loan, idx) in reportData.preprocessed_accountInfo.CLOSED.CLOSED" :key="idx">
                      <td>{{ loan.lenderName }}</td>
                      <td>{{ loan.accountNumber }}</td>
                      <td>{{ loan.openDate }}</td>
                      <td><span class="status-badge status-active">Active</span></td>
                      <td class="text-right">{{ loan.sanctionedAmount| toINR }}</td>
                      <td class="text-right">{{ loan.outstandingAmount| toINR }}</td>
                      <td>{{ loan.lastPaymentDate }}</td>
                      <td><span class="loan-badge">{{ loan.debtType }}</span></td>
                    </tr>
                  </template>
                  <tr v-else>
                    <td colspan="8" class="no-data-available">No Data available</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div> -->
      </div>
    </div>

    <!-- <div class="section payment-section-main">
      <h1 class="main-title">Payment History Information</h1>
      <div class="section-container">
        <div >
          <template v-if="reportData.preprocessed_accountInfo && reportData.preprocessed_accountInfo.ACTIVE">
            <div v-for="type in ['SECURED', 'UNSECURED', 'OTHERS']" :key="'hist-'+type">
              <h6 class="sub-title">Active {{ type === 'OTHERS' ? 'Other' : type.charAt(0) + type.slice(1).toLowerCase() }} Loan</h6>
              
              <template v-if="hasLoanData('ACTIVE', type)">
                <div v-for="(loan, idx) in reportData.preprocessed_accountInfo.ACTIVE[type]" :key="idx" class="card-wrapper">
                  <div class="card card-account-details">
                    <div class="card-header">Account Details</div>
                    <div class="details-grid">
                      <div class="detail-row"><span class="detail-label">Account Open Date</span><span class="detail-value">{{ loan.openDate }}</span></div>
                      <div class="detail-row"><span class="detail-label">Account Number</span><span class="detail-value">{{ loan.accountNumber }}</span></div>
                      <div class="detail-row"><span class="detail-label">Creditor Name</span><span class="detail-value">{{ loan.lenderName }}</span></div>
                      <div class="detail-row"><span class="detail-label">Account Holder Type</span><span class="detail-value">{{ loan.AccountHoldertypeCode }}</span></div>
                      <div class="detail-row">
                        <span class="detail-label">Date Closed</span>
                        <span class="detail-value">{{ loan.closedDate || 'N/A' }}</span>
                      </div>
                      <div class="detail-row"><span class="detail-label">Total Credit Limit</span><span class="detail-value">{{ loan.creditLimitAmount }}</span></div>
                      <div class="detail-row"><span class="detail-label">Account Status</span><span class="status-badge status-active">ACTIVE</span></div>
                    </div>
                  </div>

                  <div class="card">
                    <div class="card-header">Payment History</div>
                    <div class="details-grid">
                      <div class="detail-row"><span class="detail-label">Last payment date</span><span class="detail-value">{{ loan.lastPaymentDate }}</span></div>
                      <div class="detail-row"><span class="detail-label">Amount overdue</span><span class="detail-value">{{ loan.overdueAmount }}</span></div>
                      <div class="detail-row"><span class="detail-label">No. of missed payments</span><span class="detail-value">{{ loan.NumberOfMissedPayments }}</span></div>
                      <div class="detail-row"><span class="detail-label">No. of on time payments</span><span class="detail-value">{{ loan.NumberOfOnTimePayments }}</span></div>
                    </div>
                    <div style="margin-top: 30px;" v-if="loan.Preprocessed_Payment_History">
                      <div class="payment-history-grid border-line">
                        <span class="year-label"></span>
                        <span v-for="(m, i) in ['J','F','M','A','M','J','J','A','S','O','N','D']" :key="'sl'+m+i" class="month-label">{{ m }}</span>
                      </div>
                      <div v-for="(hist, hIdx) in loan.Preprocessed_Payment_History" :key="hIdx" class="payment-history-grid" style="margin-top: 15px;">
                        <span class="year-label">{{ hist.year }}</span>
                        <span v-for="(month, mIdx) in hist.months" :key="mIdx">
                          <span v-if="month.isOnTime" class="check-mark">✔</span>
                          <span v-else-if="month.isMissed" class="cross-mark">✘</span>
                          <span v-else class="hypen-mark">-</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <p v-else class="no-data-available" style="padding: 20px 0;">No Data available</p>
            </div>
          </template>

          <template v-if="reportData.preprocessed_accountInfo && reportData.preprocessed_accountInfo.CLOSED">
            <h6 class="sub-title">Closed Loan</h6>
            <template v-if="hasLoanData('CLOSED', 'CLOSED')">
              <div v-for="(loan, idx) in reportData.preprocessed_accountInfo.CLOSED.CLOSED" :key="'closed-hist-'+idx" class="card-wrapper">
                <div class="card card-account-details">
                  <div class="card-header">Account Details</div>
                  <div class="details-grid">
                    <div class="detail-row"><span class="detail-label">Account Open Date</span><span class="detail-value">{{ loan.openDate }}</span></div>
                    <div class="detail-row"><span class="detail-label">Account Number</span><span class="detail-value">{{ loan.accountNumber }}</span></div>
                    <div class="detail-row"><span class="detail-label">Creditor Name</span><span class="detail-value">{{ loan.lenderName }}</span></div>
                    <div class="detail-row"><span class="detail-label">Account Status</span><span class="status-badge status-closed">CLOSED</span></div>
                  </div>
                </div>
                <div class="card">
                  <div class="card-header">Payment History</div>
                  <div v-if="loan.Preprocessed_Payment_History">
                    <div class="payment-history-grid border-line">
                      <span class="year-label"></span>
                      <span v-for="(m, i) in ['J','F','M','A','M','J','J','A','S','O','N','D']" :key="'cr'+m+i" class="month-label">{{ m }}</span>
                    </div>
                    <div v-for="(hist, hIdx) in loan.Preprocessed_Payment_History" :key="'c-h-'+hIdx" class="payment-history-grid" style="margin-top: 15px;">
                      <span class="year-label">{{ hist.year }}</span>
                      <span v-for="(month, mIdx) in hist.months" :key="mIdx">
                        <span v-if="month.isOnTime" class="check-mark">✔</span>
                        <span v-else-if="month.isMissed" class="cross-mark">✘</span>
                        <span v-else class="hypen-mark">-</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <p v-else class="no-data-available" style="padding: 20px 0;">No Data available</p>
          </template>
        </div>
      </div>
    </div> -->
  </div>
</template>

<script>
export default {
  name: 'CreditReport',
  props: {
    reportData: { type: Object, required: true }
  },
  methods: {
    hasLoanData(status, type) {
      return (
        this.reportData.preprocessed_accountInfo &&
        this.reportData.preprocessed_accountInfo[status] &&
        this.reportData.preprocessed_accountInfo[status][type] &&
        this.reportData.preprocessed_accountInfo[status][type].length > 0
      );
    }
  }
}
</script>

<style scoped>
/* PORTED CSS CLASSES FROM MUSTACHE FILE */
.report-body { font-family: "Segoe UI", Tahoma, sans-serif; background-color: #F0F4F8; padding: 40px; color: #333; display: flex; flex-direction: column !important; }
.header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
.report-title h1 { color: #004479; margin: 0; font-size: 28px; font-weight: bold; padding: 10px 0; border-bottom: 1px solid #ccc; margin-bottom: 20px; }
.section { margin: 20px 0; border: 1px solid #ddd; border-radius: 8px; background: #fff; }
.section-content { padding: 20px; }
.info-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
.info-table td { border: 1px solid #e0e0e0; padding: 12px; font-size: 14px; }
.info-label { font-weight: bold; color: #F16C23; background: #f5f5f5; width: 200px; }
.info-value { color: #004479; background: #fff; }
.summary-boxes { display: flex; flex-wrap: wrap; gap: 18px; margin: 24px 0; justify-content: center; }
.summary-box { background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 8px; min-width: 150px; padding: 18px 20px; text-align: center; flex: 1 1 150px; max-width: 200px; }
.summary-label { font-size: 14px; color: #F16C23; font-weight: bold; margin-bottom: 6px; display: block; }
.summary-value { font-size: 28px; color: #004479; font-weight: bold; }
.main-title { color: #0A2342; font-size: 2.5rem; font-weight: 700; margin-bottom: 30px; text-align: center; }
.section-header { padding: 20px 30px; font-size: 1.5rem; font-weight: 700; color: #FFFFFF; border-radius: 12px 12px 0 0; }
.section-header.active { background-color: #4F6D7A; }
.section-header.closed { background-color: #8A92A8; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.90rem; outline: 1px solid #ccc; border-radius: 10px; overflow: hidden; }
.data-table th, .data-table td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #E2E8F0; }
.data-table thead { background-color: #F0F4F8; }
.data-table th { color: #4F6D7A; font-weight: 500; }
.data-table tbody tr:nth-child(even) { background-color: #EAF4F7; }
.status-badge { display: inline-block; padding: 4px 10px; border-radius: 20px; font-size: 0.8rem; font-weight: 500; }
.status-active { background-color: rgba(76, 175, 80, 0.1); color: #4CAF50; }
.status-closed { background-color: rgba(211, 47, 47, 0.1); color: #D32F2F; }
.card-wrapper { display: flex; justify-content: space-between; margin-bottom: 30px; gap: 15px; }
.card { background-color: #fff; border-radius: 12px; padding: 30px; flex: 1; border: 1px solid #eef2f5; }
.card-account-details { width: 50%; }
.card-header { font-size: 1.1rem; font-weight: 600; margin-bottom: 8px; color: #1e3a5f; border-bottom: 2px solid #eef2f5; padding-bottom: 15px; }
.detail-row { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #eef2f5; }
.detail-label { font-size: 14px; font-weight: 500; color: #6a7c92; }
.detail-value { font-size: 14px; color: #121111; }
.payment-history-grid { display: grid; grid-template-columns: repeat(13, 1fr); gap: 5px; text-align: center; }
.year-label { font-weight: 500; text-align: left; padding-left: 10px; }
.month-label { font-size: 0.8rem; color: #6a7c92; }
.check-mark { color: #28a745; font-size: 1.2rem; }
.cross-mark { color: #dc3545; font-size: 1.2rem; }
.hypen-mark { color: #333; font-size: 1rem; }
.sub-title { font-size: 1.2rem; font-weight: 600; margin-bottom: 25px; color: #1e3a5f; padding-bottom: 15px; }
.border-line { border-bottom: 1px solid rgb(237, 237, 237); margin-bottom: 8px; }
.loan-badge { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 10px; font-weight: 500; color: #4285F4; background-color: #d1e2f9; }
.no-data-available { text-align: center; font-style: italic; color: #6c757d; background-color: #f8f9fa; padding: 20px; }
</style>